import * as React from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

import { useConsent } from "./ConsentProvider";

/**
 * SPEC-COOKIE-2 — granular consent manager. Three category rows:
 *
 *   • Zorunlu Çerezler  — always on, Switch checked + disabled.
 *   • Performans / Analitik  — opt-in, off by default.
 *   • Pazarlama / Hedefleme  — opt-in, off by default.
 *
 * Initial toggle values come from the current decision (if any); closing
 * the modal without saving leaves the existing decision intact.
 */
export function CookieConsentModal() {
  const { t } = useTranslation();
  const {
    decision,
    managerOpen,
    setManagerOpen,
    acceptAll,
    rejectAll,
    saveCustom,
  } = useConsent();

  const [analytics, setAnalytics] = React.useState<boolean>(
    decision?.analytics === true,
  );
  const [marketing, setMarketing] = React.useState<boolean>(
    decision?.marketing === true,
  );

  // Sync the toggles with the active decision whenever the modal opens —
  // otherwise the toggles could lag behind a decision saved via the
  // banner buttons.
  React.useEffect(() => {
    if (managerOpen) {
      setAnalytics(decision?.analytics === true);
      setMarketing(decision?.marketing === true);
    }
  }, [managerOpen, decision]);

  return (
    <Dialog open={managerOpen} onOpenChange={setManagerOpen}>
      <DialogContent
        className="max-w-lg"
        data-testid="cookie-consent-modal"
      >
        <DialogHeader>
          <DialogTitle>{t("consent.modal.title")}</DialogTitle>
          <DialogDescription>
            {t("consent.modal.description", {
              defaultValue:
                "Hangi çerez kategorilerine izin vermek istediğinizi seçin. Zorunlu çerezler sitenin temel işlevleri için her zaman aktiftir.",
            })}
          </DialogDescription>
        </DialogHeader>

        <ul className="space-y-3">
          <li
            data-testid="cookie-cat-necessary"
            className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-start sm:justify-between"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium">
                {t("consent.modal.necessary.title")}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("consent.modal.necessary.desc")}
              </p>
            </div>
            <div className="flex items-center gap-2 sm:shrink-0">
              <Switch
                checked
                disabled
                aria-label={t("consent.modal.necessary.title")}
                data-testid="cookie-cat-necessary-switch"
              />
            </div>
          </li>

          <li
            data-testid="cookie-cat-analytics"
            className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-start sm:justify-between"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium">
                {t("consent.modal.analytics.title")}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("consent.modal.analytics.desc")}
              </p>
            </div>
            <div className="flex items-center gap-2 sm:shrink-0">
              <Switch
                checked={analytics}
                onCheckedChange={setAnalytics}
                aria-label={t("consent.modal.analytics.title")}
                data-testid="cookie-cat-analytics-switch"
              />
            </div>
          </li>

          <li
            data-testid="cookie-cat-marketing"
            className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-start sm:justify-between"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium">
                {t("consent.modal.marketing.title")}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("consent.modal.marketing.desc")}
              </p>
            </div>
            <div className="flex items-center gap-2 sm:shrink-0">
              <Switch
                checked={marketing}
                onCheckedChange={setMarketing}
                aria-label={t("consent.modal.marketing.title")}
                data-testid="cookie-cat-marketing-switch"
              />
            </div>
          </li>
        </ul>

        <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={rejectAll}
            data-testid="cookie-modal-reject-all"
          >
            {t("consent.modal.rejectAll")}
          </Button>
          <Button
            variant="outline"
            onClick={acceptAll}
            data-testid="cookie-modal-accept-all"
          >
            {t("consent.modal.acceptAll")}
          </Button>
          <Button
            onClick={() => saveCustom({ analytics, marketing })}
            data-testid="cookie-save-custom"
          >
            {t("consent.modal.saveCustom")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
